import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PermissionModeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/permission-gps.enum';
import { t } from 'app/core/i18n/i18n';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { SelectionChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/grid-events.interface';
import { PageChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/pagination-state.interface';
import { SortChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/sort-state.interface';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import {
  formatUseGpsMode,
  formatUseGpsModeChipHtml,
  formatUseGpsValidationChipHtml,
  useGpsModeSelectLabel,
  USE_GPS_MODE_OPTIONS,
} from '../../permission-gps.constants';
import { BulkGpsPartnerCellRendererComponent } from './bulk-gps-partner-cell-renderer.component';
import { AssignedPartnersSubRoutePagination } from '../../sub-route-partners.types';
import { BULK_GPS_CONFIG_MAX_PARTNERS } from 'app/modules/components/business-partners/business-partner/interfaces/bulk-gps-config.interface';
import { BulkGpsUpdateDialogService } from './bulk-gps-update-dialog.service';
import {
  BulkGpsUpdateDialogData,
  BulkGpsUpdateResult,
  BulkGpsUpdateStep,
} from './bulk-gps-update-dialog.types';

@Component({
  selector: 'app-bulk-gps-update-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    FormSectionComponent,
    ToggleOptionComponent,
    SmartGridComponent,
    TranslatePipe,
  ],
  providers: [BulkGpsUpdateDialogService],
  templateUrl: './bulk-gps-update-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkGpsUpdateDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<BulkGpsUpdateDialogComponent>);
  readonly dialogData = inject<BulkGpsUpdateDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly dialogService = inject(BulkGpsUpdateDialogService);
  private readonly handlerMessage = inject(HandlerMessageService);
  private readonly i18n = inject(LanguageService);

  readonly BulkGpsUpdateStep = BulkGpsUpdateStep;
  readonly useGpsModeOptions = USE_GPS_MODE_OPTIONS;
  readonly maxPartnersPerRequest = BULK_GPS_CONFIG_MAX_PARTNERS;

  readonly step = signal<BulkGpsUpdateStep>(BulkGpsUpdateStep.CONFIGURE);
  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly searchTerm = signal('');
  readonly selectedPartners = signal<BusinessPartnerBySubRoute[]>([]);
  readonly partners = signal<BusinessPartnerBySubRoute[]>([]);
  readonly totalItems = signal(0);
  readonly result = signal<BulkGpsUpdateResult | null>(null);

  private readonly _pagination = signal<AssignedPartnersSubRoutePagination>({
    currentPage: 1,
    limitPerPage: 50,
    sort: 'name',
    sortDirection: 'ASC',
    search: '',
  });

  readonly pagination = computed(() => this._pagination());
  readonly pageIndex = computed(() => (this.pagination().currentPage ?? 1) - 1);
  readonly selectedCount = computed(() => this.selectedPartners().length);

  readonly gpsForm = this.fb.group({
    useGps: [true],
    useGpsMode: [PermissionModeEnum.ROUTE_BASED, Validators.required],
    distanceBtwSellerAndCustomer: [0, [Validators.required, Validators.min(0)]],
  });

  readonly gridConfig: GridConfig<BusinessPartnerBySubRoute> = {
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [25, 50, 100],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api',
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      rememberSelection: true,
    },
    styling: {
      fontSize: '12px',
      maxHeight: 'min(50vh, 420px)',
      minHeight: '240px',
    },
    responsive: {
      enabled: true,
    },
    columnVisibility: {
      enabled: false,
    },
  };

  readonly colDefs: ColDef<BusinessPartnerBySubRoute>[] = [
    {
      colId: 'client',
      headerName: 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.COL_CLIENT',
      cellRenderer: BulkGpsPartnerCellRendererComponent,
      sortable: true,
      minWidth: 220,
      flex: 1,
    },
    {
      field: 'useGps',
      headerName: 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.COL_GPS_VALIDATION',
      sortable: false,
      minWidth: 130,
      flex: 0.6,
      cellInnerHtml: true,
      valueFormatter: (_value, row) => formatUseGpsValidationChipHtml(row?.useGps),
      filterOptions: {
        displayNameGetter: (value) =>
          value
            ? t('routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.CHIP_ACTIVE')
            : t('routing.SUB_ROUTE_PARTNERS.PERMISSION_GPS.CHIP_INACTIVE'),
      },
    },
    {
      field: 'useGpsMode',
      headerName: 'routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.COL_GPS_MODE_CURRENT',
      sortable: false,
      minWidth: 160,
      flex: 0.8,
      cellInnerHtml: true,
      valueFormatter: (_value, row) => formatUseGpsModeChipHtml(row?.useGpsMode),
      filterOptions: {
        displayNameGetter: (value) => formatUseGpsMode(value),
      },
    },
  ];

  getUseGpsModeSelectLabel(value: PermissionModeEnum): string {
    return useGpsModeSelectLabel(value);
  }

  readonly confirmSummary = computed(() => {
    const form = this.gpsForm.getRawValue();
    return {
      selectedCount: this.selectedCount(),
      useGps: form.useGps,
      useGpsModeLabel: formatUseGpsMode(form.useGpsMode),
      distance: form.distanceBtwSellerAndCustomer,
    };
  });

  constructor() {
    this.loadPartners();

    this.gpsForm
      .get('useGps')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((useGps) => {
        const modeControl = this.gpsForm.get('useGpsMode');
        const distanceControl = this.gpsForm.get('distanceBtwSellerAndCustomer');
        if (useGps) {
          modeControl?.enable();
          distanceControl?.enable();
        } else {
          modeControl?.disable();
          distanceControl?.disable();
        }
      });
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this._pagination.update((prev) => ({ ...prev, search: value, currentPage: 1 }));
    this.loadPartners();
  }

  onSelectionChange(event: SelectionChangedEvent<BusinessPartnerBySubRoute>): void {
    this.selectedPartners.set(event.selectedRows);
  }

  onPageChange(event: PageChangedEvent): void {
    this._pagination.update((prev) => ({
      ...prev,
      currentPage: event.pageIndex + 1,
      limitPerPage: event.pageSize,
    }));
    this.loadPartners();
  }

  onSortChange(event: SortChangedEvent<BusinessPartnerBySubRoute>): void {
    if (event.sortStates.length === 0) {
      return;
    }
    const sort = event.sortStates[0];
    this._pagination.update((prev) => ({
      ...prev,
      sort: sort.field as AssignedPartnersSubRoutePagination['sort'],
      sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
    }));
    this.loadPartners();
  }

  goToConfirm(): void {
    if (this.selectedCount() === 0) {
      this.handlerMessage.warning({
        message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.WARN_SELECT_AT_LEAST_ONE'),
      });
      return;
    }

    if (this.selectedCount() > this.maxPartnersPerRequest) {
      this.handlerMessage.warning({
        message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.WARN_MAX_PARTNERS', {
          max: this.maxPartnersPerRequest,
        }),
      });
      return;
    }

    if (this.gpsForm.get('useGps')?.value && this.gpsForm.invalid) {
      this.gpsForm.markAllAsTouched();
      this.handlerMessage.warning({
        message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.WARN_COMPLETE_CONFIG'),
      });
      return;
    }

    this.step.set(BulkGpsUpdateStep.CONFIRM);
  }

  backToConfigure(): void {
    this.step.set(BulkGpsUpdateStep.CONFIGURE);
  }

  confirmUpdate(): void {
    const selected = this.selectedPartners();
    const form = this.gpsForm.getRawValue();

    if (selected.length > this.maxPartnersPerRequest) {
      this.handlerMessage.warning({
        message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.WARN_MAX_PARTNERS', {
          max: this.maxPartnersPerRequest,
        }),
      });
      return;
    }

    this.submitting.set(true);
    this.dialogService
      .submitBulkUpdate(
        {
          subRouteId: this.dialogData.subRouteId,
          businessPartnerIds: selected.map((p) => p._id),
          useGps: form.useGps ?? false,
          useGpsMode: form.useGpsMode ?? PermissionModeEnum.ROUTE_BASED,
          distanceBtwSellerAndCustomer: form.distanceBtwSellerAndCustomer ?? 0,
        },
        selected
      )
      .subscribe({
        next: (res) => {
          this.result.set(res);
          this.step.set(BulkGpsUpdateStep.RESULTS);
          this.submitting.set(false);
        },
        error: (err) => {
          this.submitting.set(false);
          this.handlerMessage.error({
            message: err?.error?.message,
            alternativeMessage: this.i18n.t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.ERROR_BULK_UPDATE'),
            statusCode: err?.status,
          });
        },
      });
  }

  close(refresh = false): void {
    this.dialogRef.close(refresh ? { refreshed: true } : undefined);
  }

  private loadPartners(): void {
    this.loading.set(true);
    this.dialogService
      .findAssignedPartners(this.dialogData.subRouteId, this._pagination(), this.dialogData.companyId ?? null)
      .subscribe({
        next: (response) => {
          if (response?.data) {
            this.partners.set(response.data.data ?? []);
            const pageInfo = response.data.pageInfo as unknown as {
              _id: string | null;
              total: number;
            }[];
            this.totalItems.set(pageInfo[0]?.total ?? 0);
          }
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.handlerMessage.error({
            message: err?.error?.message,
            alternativeMessage: this.i18n.t('routing.SUB_ROUTE_PARTNERS.DIALOGS.BULK_GPS.ERROR_LOAD_PARTNERS'),
          });
        },
      });
  }
}
